import type { FeedbackCue, SoundFeedbackPort } from "./feedbackPorts";

type ToneRecipe = {
  readonly durationMs: number;
  readonly endFrequency: number;
  readonly peakGain: number;
  readonly startFrequency: number;
  readonly wave: OscillatorType;
};

type ActiveVoice = {
  readonly gain: GainNode;
  readonly oscillator: OscillatorNode;
};

export type WebSoundFeedbackOptions = {
  readonly enabled: boolean;
  /** Primarily a test seam; omit to use the browser AudioContext. */
  readonly contextFactory?: () => AudioContext;
};

const SILENCE = 0.0001;

const TONES: Readonly<Record<FeedbackCue, ToneRecipe>> = {
  press: {
    durationMs: 42,
    startFrequency: 720,
    endFrequency: 620,
    peakGain: 0.035,
    wave: "sine",
  },
  contact: {
    durationMs: 90,
    startFrequency: 210,
    endFrequency: 125,
    peakGain: 0.075,
    wave: "triangle",
  },
  scrape: {
    durationMs: 165,
    startFrequency: 155,
    endFrequency: 82,
    peakGain: 0.025,
    wave: "sawtooth",
  },
  lift: {
    durationMs: 125,
    startFrequency: 330,
    endFrequency: 510,
    peakGain: 0.04,
    wave: "sine",
  },
  confirm: {
    durationMs: 180,
    startFrequency: 440,
    endFrequency: 660,
    peakGain: 0.05,
    wave: "sine",
  },
  alert: {
    durationMs: 155,
    startFrequency: 245,
    endFrequency: 185,
    peakGain: 0.045,
    wave: "triangle",
  },
};

function getBrowserContextFactory(): (() => AudioContext) | undefined {
  let BrowserAudioContext: typeof AudioContext;
  try {
    if (typeof globalThis.AudioContext !== "function") {
      return undefined;
    }
    BrowserAudioContext = globalThis.AudioContext;
  } catch {
    return undefined;
  }

  return () => new BrowserAudioContext({ latencyHint: "interactive" });
}

async function closeContext(context: AudioContext): Promise<void> {
  try {
    if (context.state !== "closed") {
      await context.close();
    }
  } catch {
    // Feedback must never prevent navigation, restart, or gameplay cleanup.
  }
}

export function createWebSoundFeedback({
  enabled,
  contextFactory = getBrowserContextFactory(),
}: WebSoundFeedbackOptions): SoundFeedbackPort {
  let isEnabled = enabled;
  let isDisposed = false;
  let context: AudioContext | undefined;
  let output: GainNode | undefined;
  let disposal: Promise<void> | undefined;
  const voices = new Set<ActiveVoice>();

  const releaseVoice = (voice: ActiveVoice, stop: boolean): void => {
    if (!voices.delete(voice)) {
      return;
    }

    voice.oscillator.onended = null;
    if (stop) {
      try {
        voice.oscillator.stop();
      } catch {
        // A voice that already ended needs no further cleanup.
      }
    }

    try {
      voice.oscillator.disconnect();
    } catch {
      // Some browser implementations throw when an unconnected node disconnects.
    }
    try {
      voice.gain.disconnect();
    } catch {
      // See above; cleanup remains best-effort and failure-safe.
    }
  };

  const stopAllVoices = (): void => {
    for (const voice of [...voices]) {
      releaseVoice(voice, true);
    }
  };

  const setOutputLevel = (audible: boolean): void => {
    if (context === undefined || output === undefined) {
      return;
    }

    try {
      output.gain.cancelScheduledValues(context.currentTime);
      output.gain.setValueAtTime(audible ? 1 : 0, context.currentTime);
    } catch {
      // A broken audio graph degrades to silence.
    }
  };

  return {
    get available() {
      return contextFactory !== undefined;
    },
    get enabled() {
      return isEnabled;
    },
    setEnabled(nextEnabled) {
      if (isDisposed || nextEnabled === isEnabled) {
        return;
      }

      isEnabled = nextEnabled;
      if (!isEnabled) {
        stopAllVoices();
      }
      setOutputLevel(isEnabled);
    },
    async activate() {
      if (isDisposed || !isEnabled || contextFactory === undefined) {
        return false;
      }

      let needsContext = context === undefined;
      if (!needsContext) {
        try {
          needsContext = context?.state === "closed";
        } catch {
          needsContext = true;
        }
      }

      if (needsContext) {
        try {
          output?.disconnect();
        } catch {
          // A closed or broken graph is replaced below.
        }
        context = undefined;
        output = undefined;
        let nextContext: AudioContext | undefined;
        let nextOutput: GainNode | undefined;

        try {
          // This construction is intentionally synchronous before the first await
          // so callers can invoke activate() inside a mobile user gesture.
          nextContext = contextFactory();
          nextOutput = nextContext.createGain();
          nextOutput.gain.value = 1;
          nextOutput.connect(nextContext.destination);
          context = nextContext;
          output = nextOutput;
        } catch {
          try {
            nextOutput?.disconnect();
          } catch {
            // Failed construction already means silent degradation.
          }
          if (nextContext !== undefined) {
            await closeContext(nextContext);
          }
          return false;
        }
      }

      const activeContext = context;
      if (activeContext === undefined) {
        return false;
      }

      try {
        if (activeContext.state !== "running") {
          await activeContext.resume();
        }
        return context === activeContext && activeContext.state === "running";
      } catch {
        return false;
      }
    },
    emit(cue) {
      if (isDisposed || !isEnabled || context === undefined || output === undefined) {
        return;
      }

      const activeContext = context;
      const activeOutput = output;
      let oscillator: OscillatorNode | undefined;
      let cueGain: GainNode | undefined;
      let voice: ActiveVoice | undefined;
      try {
        if (activeContext.state !== "running") {
          return;
        }

        const recipe = TONES[cue];
        const start = activeContext.currentTime;
        const end = start + recipe.durationMs / 1000;
        oscillator = activeContext.createOscillator();
        cueGain = activeContext.createGain();

        oscillator.type = recipe.wave;
        oscillator.frequency.setValueAtTime(recipe.startFrequency, start);
        oscillator.frequency.exponentialRampToValueAtTime(recipe.endFrequency, end);
        cueGain.gain.setValueAtTime(SILENCE, start);
        cueGain.gain.linearRampToValueAtTime(recipe.peakGain, start + 0.012);
        cueGain.gain.exponentialRampToValueAtTime(SILENCE, end);
        oscillator.connect(cueGain);
        cueGain.connect(activeOutput);

        const activeVoice: ActiveVoice = { gain: cueGain, oscillator };
        voice = activeVoice;
        voices.add(activeVoice);
        oscillator.onended = () => {
          releaseVoice(activeVoice, false);
        };
        oscillator.start(start);
        oscillator.stop(end + 0.01);
      } catch {
        if (voice !== undefined) {
          releaseVoice(voice, true);
        } else if (oscillator !== undefined && cueGain !== undefined) {
          try {
            oscillator.disconnect();
            cueGain.disconnect();
          } catch {
            // A partially-created graph is already silent.
          }
        }
      }
    },
    dispose() {
      if (disposal !== undefined) {
        return disposal;
      }

      isDisposed = true;
      isEnabled = false;
      stopAllVoices();
      const contextToClose = context;
      context = undefined;
      try {
        output?.disconnect();
      } catch {
        // Cleanup failure cannot escape the feedback boundary.
      }
      output = undefined;
      disposal = contextToClose === undefined ? Promise.resolve() : closeContext(contextToClose);
      return disposal;
    },
  };
}
